/**
 * How many decimals a node shows, or null to print the value untouched.
 *
 * Null is a real choice rather than a missing one: the values come from
 * user-written `f()` code as strings, so leaving them alone is the only way to
 * see exactly what that code returned.
 */
type DecimalPlaces = number | null;

/**
 * The decimal counts offered in the settings.
 */
const decimalPlacesOptions: DecimalPlaces[] = [null, 0, 1, 2, 3, 4, 6];

/**
 * Formats a value for display.
 *
 * Anything that isn't a finite number is passed through as it is. A node can
 * legitimately hold `Infinity`, `NaN` or a string an operation returned, and
 * rounding those would either throw or replace a meaningful result with a
 * meaningless one.
 *
 * This is display only: the string kept in the core graph stays exact, so the
 * setting never changes what the next `f()` or `dfdx()` is given.
 *
 * @param value The value as the core graph holds it.
 * @param decimalPlaces How many decimals to show, or null to leave it alone.
 * @returns The value to print.
 */
const formatNumber = (value: string, decimalPlaces: DecimalPlaces): string => {
  if (decimalPlaces === null) {
    return value;
  }

  // `Number("")` is 0, which would turn an empty value into a fake zero
  if (value.trim() === "") {
    return value;
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return value;
  }

  return parsedValue.toFixed(decimalPlaces);
};

/**
 * Names a decimal count for the settings menu.
 * @param decimalPlaces The count, or null.
 * @returns The label to show.
 */
const getDecimalPlacesLabel = (decimalPlaces: DecimalPlaces): string => {
  return decimalPlaces === null ? "Auto" : `${decimalPlaces}`;
};

export { decimalPlacesOptions, formatNumber, getDecimalPlacesLabel };
export type { DecimalPlaces };
